import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import type { ClientToServerEvents, ServerToClientEvents } from '../types/realtime';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface RealtimeContextValue {
  socket: TypedSocket | null;
  isConnected: boolean;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

const DEFAULT_SOCKET_URL = 'http://localhost:3000';

const resolveSocketUrl = () => {
  const explicit = import.meta.env.VITE_SOCKET_URL as string | undefined;
  if (explicit && explicit.length > 0) {
    return explicit;
  }

  return DEFAULT_SOCKET_URL;
};

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, tokens } = useAuth();
  const [socket, setSocket] = useState<TypedSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<TypedSocket | null>(null);
  const urlRef = useRef<string>(resolveSocketUrl());

  const disconnectCurrentSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSocket(null);
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !tokens?.accessToken) {
      disconnectCurrentSocket();
      return;
    }

    const connection = io(urlRef.current, {
      transports: ['websocket'],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      auth: {
        token: tokens.accessToken,
      },
    });

    socketRef.current = connection;

    connection.on('connect', () => {
      setIsConnected(true);
    });

    connection.on('disconnect', () => {
      setIsConnected(false);
    });

    connection.on('connect_error', (error: Error) => {
      console.warn('Realtime connection error', error);
    });

    connection.on('server:pong', () => {
      // keep-alive acknowledgement, no-op for now
    });

    connection.connect();
    setSocket(connection);

    return () => {
      connection.removeAllListeners();
      connection.disconnect();
      if (socketRef.current === connection) {
        socketRef.current = null;
      }
      setIsConnected(false);
    };
  }, [disconnectCurrentSocket, isAuthenticated, tokens?.accessToken]);

  const value = useMemo<RealtimeContextValue>(() => ({
    socket,
    isConnected,
  }), [socket, isConnected]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};
